import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Loader from "../Live/Loader";
import {
  FiBook,
  FiUsers,
  FiFileText,
  FiVideo,
  FiFolder,
  FiCheckCircle,
  FiClock,
  FiChevronDown,
  FiChevronUp,
  FiActivity,
  FiDollarSign,
  FiPlay,
  FiAward,
} from "react-icons/fi";

const InstructorHistory = ({ instructorData, loading: externalLoading }) => {
  const [activeTab, setActiveTab] = useState("overview");
  const [expandedCourse, setExpandedCourse] = useState(null);

  // Extract history data from instructorData (comes from backend API)
  const historyData = useMemo(() => {
    if (!instructorData?.history) {
      return {
        courses: [],
        lessons: [],
        materials: [],
        exams: [],
        statistics: null,
      };
    }

    return {
      courses: instructorData.history.courses || [],
      lessons: instructorData.history.lessons || [],
      materials: instructorData.history.materials || [],
      exams: instructorData.history.exams || [],
      statistics: instructorData.history.statistics || null,
    };
  }, [instructorData]);

  const tabs = [
    { id: "overview", label: "Overview", icon: FiActivity },
    { id: "courses", label: "Courses", icon: FiBook, count: historyData.courses.length },
    { id: "lessons", label: "Lessons", icon: FiVideo, count: historyData.lessons.length },
    { id: "materials", label: "Materials", icon: FiFolder, count: historyData.materials.length },
    { id: "exams", label: "Exams", icon: FiAward, count: historyData.exams.length },
  ];

  // Use statistics from backend or calculate from local data
  const stats = useMemo(() => {
    if (historyData.statistics) {
      return {
        totalCourses: historyData.statistics.courses?.total || 0,
        activeCourses: historyData.statistics.courses?.active || 0,
        totalStudents: historyData.statistics.students?.total || 0,
        totalLessons: historyData.statistics.lessons?.total || 0,
        totalMaterials: historyData.statistics.materials?.total || 0,
        totalExams: historyData.statistics.exams?.total || 0,
        publishedExams: historyData.statistics.exams?.published || 0,
        totalLiveSessions: historyData.statistics.live_sessions?.total || 0,
        completedLiveSessions: historyData.statistics.live_sessions?.completed || 0,
        totalPayments: historyData.statistics.payments?.total || 0,
      };
    }

    // Fallback to local calculation if statistics not provided
    const liveSessions = historyData.lessons.filter(l => l.has_live_session);
    return {
      totalCourses: historyData.courses.length,
      activeCourses: historyData.courses.filter(c => c.status === 'active').length,
      totalStudents: historyData.courses.reduce((sum, c) => sum + (c.students_count || 0), 0),
      totalLessons: historyData.lessons.length,
      totalMaterials: historyData.materials.length,
      totalExams: historyData.exams.length,
      publishedExams: historyData.exams.filter(e => e.status === 'published').length,
      totalLiveSessions: liveSessions.length,
      completedLiveSessions: liveSessions.filter(l => l.live_session?.ended_at).length,
      totalPayments: historyData.courses.reduce((sum, c) => sum + (c.payments_count || 0), 0),
    };
  }, [historyData]);

  if (externalLoading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Loader />
      </div>
    );
  }

  // Check if history data exists
  if (!instructorData?.history) {
    return (
      <div className="flex flex-col items-center justify-center py-12 bg-gray-50 dark:bg-gray-700/30 rounded-xl">
        <FiActivity className="w-12 h-12 text-gray-400 mb-4" />
        <p className="text-gray-500 dark:text-gray-400">No history data available</p>
      </div>
    );
  }

  return (
    <div className="mt-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
            <FiActivity className="w-6 h-6 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Instructor History
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Complete teaching and course records
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6 border-b border-gray-200 dark:border-gray-700 pb-4">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all ${
              activeTab === tab.id
                ? "bg-purple-600 text-white shadow-md"
                : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
            }`}
          >
            <tab.icon className="w-4 h-4" />
            <span>{tab.label}</span>
            {tab.count !== undefined && (
              <span className={`px-2 py-0.5 rounded-full text-xs ${
                activeTab === tab.id
                  ? "bg-white/20 text-white"
                  : "bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300"
              }`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
        >
          {activeTab === "overview" && (
            <OverviewTab stats={stats} historyData={historyData} />
          )}
          {activeTab === "courses" && (
            <CoursesTab 
              courses={historyData.courses} 
              expandedCourse={expandedCourse}
              setExpandedCourse={setExpandedCourse}
            />
          )}
          {activeTab === "lessons" && (
            <LessonsTab lessons={historyData.lessons} />
          )}
          {activeTab === "materials" && (
            <MaterialsTab materials={historyData.materials} />
          )}
          {activeTab === "exams" && (
            <ExamsTab exams={historyData.exams} />
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

// Overview Tab Component
const OverviewTab = ({ stats, historyData }) => (
  <div className="space-y-6">
    {/* Quick Stats Cards */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <StatCard
        icon={FiBook}
        label="Total Courses"
        value={stats.totalCourses}
        subValue={`${stats.activeCourses} active`}
        color="purple"
      />
      <StatCard
        icon={FiUsers}
        label="Total Students"
        value={stats.totalStudents}
        subValue="enrolled"
        color="blue"
      />
      <StatCard
        icon={FiVideo}
        label="Lessons Created"
        value={stats.totalLessons}
        subValue={`${stats.totalLiveSessions} live sessions`}
        color="green"
      />
      <StatCard
        icon={FiAward}
        label="Exams Created"
        value={stats.totalExams}
        subValue={`${stats.publishedExams} published`}
        color="orange"
      />
    </div>

    {/* Additional Stats */}
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      <StatCard
        icon={FiFolder}
        label="Materials"
        value={stats.totalMaterials}
        subValue="uploaded"
        color="blue"
      />
      <StatCard
        icon={FiPlay}
        label="Live Sessions"
        value={stats.totalLiveSessions}
        subValue={`${stats.completedLiveSessions} completed`}
        color="green"
      />
      <StatCard
        icon={FiDollarSign}
        label="Total Enrollments"
        value={stats.totalPayments}
        subValue="payments"
        color="purple"
      />
    </div>

    {/* Recent Activity */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Recent Courses */}
      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-5">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <FiBook className="w-4 h-4 text-purple-500" />
          Recent Courses
        </h3>
        <div className="space-y-3">
          {historyData.courses.slice(0, 3).map((course, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg">
              <div>
                <p className="font-medium text-gray-900 dark:text-white">{course.title}</p>
                <p className="text-xs text-gray-500">{course.students_count || 0} students • {course.lessons_count || 0} lessons</p>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                course.status === 'active'
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                  : 'bg-gray-100 text-gray-700 dark:bg-gray-600 dark:text-gray-300'
              }`}>
                {course.status}
              </span>
            </div>
          ))}
          {historyData.courses.length === 0 && (
            <p className="text-gray-500 dark:text-gray-400 text-sm text-center py-4">No courses created</p>
          )}
        </div>
      </div>

      {/* Recent Lessons */}
      <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-5">
        <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <FiVideo className="w-4 h-4 text-green-500" />
          Recent Lessons
        </h3>
        <div className="space-y-3">
          {historyData.lessons.slice(0, 3).map((lesson, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg">
              <div>
                <p className="font-medium text-gray-900 dark:text-white">{lesson.title}</p>
                <p className="text-xs text-gray-500">{lesson.course?.title || 'Course'}</p>
              </div>
              {lesson.has_live_session && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                  Live Session
                </span>
              )}
            </div>
          ))}
          {historyData.lessons.length === 0 && (
            <p className="text-gray-500 dark:text-gray-400 text-sm text-center py-4">No lessons created</p>
          )}
        </div>
      </div>
    </div>
  </div>
);

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, subValue, color }) => {
  const colorClasses = {
    purple: "bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400",
    blue: "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400",
    green: "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400",
    orange: "bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400",
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${colorClasses[color]}`}>
        <Icon className="w-5 h-5" />
      </div>
      <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
      <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{subValue}</p>
    </div>
  );
};

// Courses Tab Component
const CoursesTab = ({ courses, expandedCourse, setExpandedCourse }) => (
  <div className="space-y-4">
    {courses.length === 0 ? (
      <EmptyState icon={FiBook} message="No courses created yet" />
    ) : (
      courses.map((course, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.05 }}
          className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
        >
          <div 
            className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50"
            onClick={() => setExpandedCourse(expandedCourse === index ? null : index)}
          >
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                course.status === 'active'
                  ? 'bg-green-100 dark:bg-green-900/30'
                  : 'bg-gray-100 dark:bg-gray-700'
              }`}>
                <FiBook className={`w-6 h-6 ${
                  course.status === 'active'
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-gray-600 dark:text-gray-400'
                }`} />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white">{course.title}</h4>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {course.students_count || 0} students • {course.lessons_count || 0} lessons
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                course.status === 'active'
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                  : 'bg-gray-100 text-gray-700 dark:bg-gray-600 dark:text-gray-300'
              }`}>
                {course.status}
              </span>
              {expandedCourse === index ? <FiChevronUp /> : <FiChevronDown />}
            </div>
          </div>
          
          <AnimatePresence>
            {expandedCourse === index && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="border-t border-gray-200 dark:border-gray-700"
              >
                <div className="p-4 grid grid-cols-2 md:grid-cols-5 gap-4 bg-gray-50 dark:bg-gray-700/30">
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Level</p>
                    <p className="font-medium text-gray-900 dark:text-white">{course.level || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Price</p>
                    <p className="font-medium text-gray-900 dark:text-white">${course.price || 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Materials</p>
                    <p className="font-medium text-gray-900 dark:text-white">{course.materials_count || 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Exams</p>
                    <p className="font-medium text-gray-900 dark:text-white">{course.exams_count || 0}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Payments</p>
                    <p className="font-medium text-gray-900 dark:text-white">{course.payments_count || 0}</p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      ))
    )}
  </div>
);

// Lessons Tab Component
const LessonsTab = ({ lessons }) => (
  <div className="space-y-4">
    {lessons.length === 0 ? (
      <EmptyState icon={FiVideo} message="No lessons created yet" />
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-100 dark:bg-gray-700">
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Lesson</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Course</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Live Session</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Recording</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Created</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {lessons.map((lesson, index) => (
              <tr key={index} className="bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50">
                <td className="px-4 py-3">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{lesson.title}</p>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                  {lesson.course?.title || 'N/A'}
                </td>
                <td className="px-4 py-3">
                  {lesson.has_live_session ? (
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                      lesson.live_session?.ended_at
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                    }`}>
                      {lesson.live_session?.ended_at ? <FiCheckCircle className="w-3 h-3" /> : <FiClock className="w-3 h-3" />}
                      {lesson.live_session?.ended_at ? 'Completed' : 'Scheduled'}
                    </span>
                  ) : (
                    <span className="text-gray-400 text-xs">No session</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {lesson.live_session?.has_recording ? (
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                      <FiPlay className="w-3 h-3" />
                      Available
                    </span>
                  ) : (
                    <span className="text-gray-400 text-xs">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                  {lesson.created_at}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

// Materials Tab Component
const MaterialsTab = ({ materials }) => (
  <div className="space-y-4">
    {materials.length === 0 ? (
      <EmptyState icon={FiFolder} message="No materials uploaded yet" />
    ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {materials.map((material, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: index * 0.05 }}
            className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                <FiFileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-gray-900 dark:text-white truncate">{material.title}</h4>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {material.course?.title || 'Course'}
                </p>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-xs text-gray-400 uppercase">{material.file_type || 'File'}</span>
                  {material.file_size && (
                    <span className="text-xs text-gray-400">• {material.file_size}</span>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    )}
  </div>
);

// Exams Tab Component
const ExamsTab = ({ exams }) => (
  <div className="space-y-4">
    {exams.length === 0 ? (
      <EmptyState icon={FiAward} message="No exams created yet" />
    ) : (
      exams.map((exam, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.05 }}
          className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                exam.status === 'published' 
                  ? 'bg-green-100 dark:bg-green-900/30'
                  : 'bg-yellow-100 dark:bg-yellow-900/30'
              }`}>
                <FiAward className={`w-5 h-5 ${
                  exam.status === 'published'
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-yellow-600 dark:text-yellow-400'
                }`} />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white">
                  {exam.title}
                </h4>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {exam.course?.title || 'Course'} • {exam.questions_count || 0} questions
                </p>
              </div>
            </div>
            <div className="text-right">
              <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${
                exam.status === 'published'
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                  : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
              }`}>
                {exam.status}
              </span>
              <p className="text-xs text-gray-500 mt-1">{exam.attempts_count || 0} attempts</p>
            </div>
          </div>
          <div className="flex items-center gap-6 mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
            <div>
              <span className="text-xs text-gray-500">Duration</span>
              <p className="text-sm font-medium text-gray-900 dark:text-white">{exam.duration_minutes || 0} min</p>
            </div>
            <div>
              <span className="text-xs text-gray-500">Pass %</span>
              <p className="text-sm font-medium text-gray-900 dark:text-white">{exam.pass_percentage || 0}%</p>
            </div>
            <div>
              <span className="text-xs text-gray-500">Code</span>
              <p className="text-sm font-medium text-gray-900 dark:text-white">{exam.exam_code || 'N/A'}</p>
            </div>
          </div>
        </motion.div>
      ))
    )}
  </div>
);

// Empty State Component
const EmptyState = ({ icon: Icon, message }) => (
  <div className="flex flex-col items-center justify-center py-12 bg-gray-50 dark:bg-gray-700/30 rounded-xl">
    <Icon className="w-12 h-12 text-gray-400 mb-4" />
    <p className="text-gray-500 dark:text-gray-400">{message}</p>
  </div>
);

export default InstructorHistory;
